// SectionDetailModal.jsx — paspaudus "Sekcija N" kortelę "Rinkinyje yra"
// skirtuke, atsidaro šis langas su didesniu 3D vaizdu, techniniu brėžiniu ir
// pilna TOS KONKREČIOS sekcijos sudėtimi (durelės, lentynos, furnitūra) —
// naudoja tą pačią useFurnitureDrawings fiksavimo logiką (perduota iš tėvinio
// FurnitureInfoTabs.jsx, kad nereikėtų antro nepriklausomo fiksavimo ciklo),
// tik parenka VIENO modulio (furnitureId grupės) raktą, o ne viso baldo.
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, tokens, Button, ToggleButton, Spinner, FluentProvider } from '@fluentui/react-components';
import { iconMap } from '../../theme/icons';
import { Card } from '../../theme/primitives';
import { fititLightTheme } from '../../theme/brand';
import { getDoorTypeLabel } from '../../configurator/summary/materialThumbnails';

const DismissIcon = iconMap.close;
const InventoryIcon = iconMap.inventory_2;

const STEP_TABS = [
    { key: 'exterior', labelKey: 'shop.section_detail_modal.tab_exterior', icon: iconMap.door_front },
    { key: 'interior', labelKey: 'shop.section_detail_modal.tab_interior', icon: iconMap.shelves },
];

// Segmentuotas 2 punktų perjungiklis ("Išorė"/"Vidus") — realūs Fluent
// <ToggleButton> viename konteineryje, ta pati vizualinė kalba, kaip
// FurnitureList.jsx `viewToggle` (3D/2D perjungiklis), o ne rankomis rašyti
// <button> elementai (žr. 18 punktų sąrašo #5 — visi mygtukai turi būti
// realūs Fluent komponentai).
const useStyles = makeStyles({
    root: { position: 'fixed', inset: 0, zIndex: tokens.zIndexOverlay, display: 'flex', flexDirection: 'column', backgroundColor: tokens.colorNeutralBackground3 },
    header: {
        display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM,
        paddingLeft: tokens.spacingHorizontalL, paddingRight: tokens.spacingHorizontalL,
        height: '64px', backgroundColor: tokens.colorNeutralBackground1,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, flexShrink: 0,
        '@media (min-width: 640px)': { paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL },
    },
    iconChip: {
        width: '36px', height: '36px', borderRadius: tokens.borderRadiusXLarge,
        backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    titleWrap: { minWidth: 0 },
    title: {
        fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0,
    },
    subtitle: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, margin: 0 },
    closeBtn: { marginLeft: 'auto' },
    body: { flex: 1, overflowY: 'auto', paddingTop: tokens.spacingVerticalXXL, paddingBottom: tokens.spacingVerticalXXL },
    inner: {
        maxWidth: '56rem', marginLeft: 'auto', marginRight: 'auto',
        paddingLeft: tokens.spacingHorizontalM, paddingRight: tokens.spacingHorizontalM,
        display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL,
        '@media (min-width: 640px)': { paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL },
    },
    viewToggle: {
        display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS,
        backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusXLarge,
        padding: tokens.spacingHorizontalXXS, border: `1px solid ${tokens.colorNeutralStroke2}`,
        width: 'fit-content',
    },
    imageGrid: {
        display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalL,
        '@media (min-width: 768px)': { gridTemplateColumns: '1fr 1fr' },
    },
    imageCard: { overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '340px' },
    imageFull: { width: '100%', height: '100%', objectFit: 'contain' },
    loadingBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalM, color: tokens.colorNeutralForeground3, paddingTop: tokens.spacingVerticalXXL, paddingBottom: tokens.spacingVerticalXXL },
    loadingLabel: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightMedium },
    compositionCard: { padding: tokens.spacingHorizontalL },
    compositionLabel: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: tokens.spacingVerticalM },
    compositionEmpty: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 },
    compositionGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: '10px', '@media (min-width: 640px)': { gridTemplateColumns: '1fr 1fr' } },
    compositionRow: { display: 'flex', alignItems: 'center', gap: '10px', paddingTop: tokens.spacingVerticalXS, paddingBottom: tokens.spacingVerticalXS },
    compositionIcon: { color: tokens.colorNeutralForeground3, flexShrink: 0 },
    compositionText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2 },
});

const SectionDetailModal = ({ module, sectionLabel, code, settings, images, capturing, onClose }) => {
    const s = useStyles();
    const { t } = useTranslation();
    const [tab, setTab] = useState('exterior');
    if (!module) return null;

    const shot = images[`${module.furnitureId}-${tab}`] || {};
    const calc = module.calc || {};
    const stats = calc.internalStats || {};

    // doorPanels don't carry the door-type (K/D/dvigubos...) directly — that
    // lives in doorColors, keyed by section path, same lookup as the
    // "Rinkinyje yra" grid's own whole-furniture door tally.
    const doorTypeCounts = (() => {
        const counts = new Map();
        (calc.doorPanels || []).forEach((d) => {
            const groupEntry = (settings.mergedSections || []).find(s2 => s2.groupId === d.groupId);
            const doorTypeValue = groupEntry
                ? settings.doorColors?.[`${groupEntry.furnitureId}-${groupEntry.cabinetIndex}-${groupEntry.sectionIndex}`]
                : null;
            const label = getDoorTypeLabel(doorTypeValue) || t('shop.furniture_info_tabs.door_fallback');
            counts.set(label, (counts.get(label) || 0) + 1);
        });
        return Array.from(counts.entries());
    })();
    const shelfCount = (module.cabinets || []).reduce((sum, c) => sum + (c.shelves?.length || 0), 0);
    const hardwareRows = [
        [t('shop.section_detail_modal.hangers'), stats.hangerCount || 0],
        [t('shop.section_detail_modal.pullouts'), stats.pullOutCount || 0],
        [t('shop.section_detail_modal.pantographs'), stats.pantographCount || 0],
        [t('shop.section_detail_modal.legs'), calc.legCount || 0],
    ].filter(([, qty]) => qty > 0);
    const compositionRows = [
        ...doorTypeCounts.map(([label, qty]) => [label, qty, iconMap.door_front]),
        ...(shelfCount > 0 ? [[t('shop.section_detail_modal.shelf'), shelfCount, iconMap.shelves]] : []),
        ...hardwareRows.map(([label, qty]) => [label, qty, iconMap.build]),
    ];

    // Rendered via portal directly under <body> — same stacking-context reasoning
    // as AssemblyInstructionsModal.jsx (public Header.jsx paints over in-tree
    // fixed overlays regardless of z-index).
    // BUG (rasta 2026-09-04, žr. 20 punktų sąrašo #8/#12): FluentProvider CSS
    // kintamieji galioja TIK jo DOM pomedžiui — createPortal į document.body
    // iš jo ištrūksta, tad visi tokens.* be šio apvyniojimo tyliai virsta
    // permatomi/undefined (žr. detalų komentarą theme/primitives.jsx SimpleModal).
    return createPortal((
        <FluentProvider theme={fititLightTheme} style={{ background: 'transparent' }}>
        <div className={s.root}>
            <div className={s.header}>
                <div className={s.iconChip}>
                    <InventoryIcon style={{ fontSize: 20 }} />
                </div>
                <div className={s.titleWrap}>
                    <p className={s.title}>{sectionLabel} — {code}</p>
                    <p className={s.subtitle}>
                        {(module.width * 100).toFixed(0)} × {(module.height * 100).toFixed(0)} × {(module.depth * 100).toFixed(0)} cm
                    </p>
                </div>
                <Button appearance="subtle" icon={<DismissIcon />} onClick={onClose} aria-label={t('shop.section_detail_modal.close')} className={s.closeBtn} />
            </div>

            <div className={s.body}>
                <div className={s.inner}>
                    <div className={s.viewToggle}>
                        {STEP_TABS.map((tabDef) => (
                            <ToggleButton
                                key={tabDef.key}
                                size="small"
                                appearance={tab === tabDef.key ? 'primary' : 'subtle'}
                                checked={tab === tabDef.key}
                                icon={<tabDef.icon fontSize={14} />}
                                onClick={() => setTab(tabDef.key)}
                            >
                                {t(tabDef.labelKey)}
                            </ToggleButton>
                        ))}
                    </div>

                    <div className={s.imageGrid}>
                        <Card className={s.imageCard}>
                            {shot.img3d ? (
                                <img src={shot.img3d} alt={`${sectionLabel} ${t('shop.section_detail_modal.alt_3d')}`} className={s.imageFull} />
                            ) : (
                                <div className={s.loadingBox}>
                                    <Spinner size="small" />
                                    <span className={s.loadingLabel}>{capturing ? t('shop.section_detail_modal.preparing_image') : t('shop.section_detail_modal.prepare_failed')}</span>
                                </div>
                            )}
                        </Card>
                        <Card className={s.imageCard}>
                            {shot.img2d ? (
                                <img src={shot.img2d} alt={`${sectionLabel} ${t('shop.section_detail_modal.alt_drawing')}`} className={s.imageFull} />
                            ) : (
                                <div className={s.loadingBox}>
                                    <Spinner size="small" />
                                    <span className={s.loadingLabel}>{capturing ? t('shop.section_detail_modal.preparing_drawing') : t('shop.section_detail_modal.prepare_failed')}</span>
                                </div>
                            )}
                        </Card>
                    </div>

                    <Card className={s.compositionCard}>
                        <p className={s.compositionLabel}>{t('shop.section_detail_modal.composition_title')}</p>
                        {compositionRows.length === 0 ? (
                            <p className={s.compositionEmpty}>{t('shop.section_detail_modal.composition_empty')}</p>
                        ) : (
                            <div className={s.compositionGrid}>
                                {compositionRows.map(([label, qty, Icon]) => (
                                    <div key={label} className={s.compositionRow}>
                                        <Icon className={s.compositionIcon} style={{ fontSize: 18 }} />
                                        <span className={s.compositionText}>{qty} × {label}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
        </FluentProvider>
    ), document.body);
};

export default SectionDetailModal;
